import { ROADSIDE_ASSISTANCE } from '@rsos/capstone/src/constants/capabilities';
import { postRoadsideAssistanceLink } from '../fetchMidasServices';
import { postLocationShareRequestUrl } from '../fetchPublicServices';
import { formatInternationalPhoneNumberHuman } from '../internationalPhoneNumberUtils';

export type SmartPhraseContext = {
  callerID?: string;
  token?: string;
  orgName?: string;
  accountId?: string;
  agencyName?: string;
  agencyState?: string;
  contactName?: string;
  contactTitle?: string;
  contactPhone?: string;
  contactEmail?: string;
  nonEmergencyPhone?: string;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
};

export type SmartPhrase = {
  key: string; // key to identify the smart phrase
  resolve: (ctx: SmartPhraseContext) => Promise<string> | string; // function to resolve the smart phrase, can be async
  mockResolveValue?: string; // when set, used in place of calling resolve (e.g. network-bound phrases that must not fire at render time)
  display: string; // value that can be displayed in the UI when the smart phrase is not resolved yet
  description: string; // description of the smart phrase for documentation purposes - useful for tooltips
  capability?: string; // optional capability required to use this smart phrase
};

export type SmartPhraseKey = (typeof smartPhrases)[number]['key'];

export type TextSegment = { type: 'text'; content: string };
export type PhraseSegment = {
  type: 'phrase';
  key: string;
  phrase?: SmartPhrase;
};
export type ParsedSegment = TextSegment | PhraseSegment;

export const isPhraseSegment = (
  segment: ParsedSegment,
): segment is PhraseSegment => {
  return segment.type === 'phrase';
};

export const isTextSegment = (
  segment: ParsedSegment,
): segment is TextSegment => {
  return segment.type === 'text';
};

export const smartPhrases = [
  {
    key: 'AgencyName',
    resolve: ({ agencyName }) => {
      if (!agencyName) {
        throw new Error('Missing required context for AgencyName');
      }

      return agencyName;
    },
    display: 'Agency Name',
    description: 'The name of your agency.',
  },
  {
    key: 'AgencyState',
    resolve: ({ agencyState }) => {
      if (!agencyState) {
        throw new Error('Missing required context for AgencyState');
      }

      return agencyState;
    },
    display: 'Agency State',
    description: 'The state or province of your agency.',
  },
  {
    key: 'ContactEmail',
    resolve: ({ contactEmail }) => {
      if (!contactEmail) {
        throw new Error('Missing required context for ContactEmail');
      }

      return contactEmail;
    },
    display: 'Contact Email',
    description: 'The point of contact email address for your agency.',
  },
  {
    key: 'ContactName',
    resolve: ({ contactName }) => {
      if (!contactName) {
        throw new Error('Missing required context for ContactName');
      }

      return contactName;
    },
    display: 'Contact Name',
    description: 'The point of contact name for your agency.',
  },
  {
    key: 'ContactPhone',
    resolve: ({ contactPhone }) => {
      if (!contactPhone) {
        throw new Error('Missing required context for ContactPhone');
      }

      return formatInternationalPhoneNumberHuman(contactPhone) ?? contactPhone;
    },
    display: 'Contact Phone',
    description: 'The point of contact phone number for your agency.',
  },
  {
    key: 'ContactTitle',
    resolve: ({ contactTitle }) => {
      if (!contactTitle) {
        throw new Error('Missing required context for ContactTitle');
      }

      return contactTitle;
    },
    display: 'Contact Title',
    description: 'The point of contact title for your agency.',
  },
  {
    key: 'LocationRequestLink',
    resolve: async ({ callerID, token, orgName }) => {
      if (!callerID || !token || !orgName) {
        throw new Error('Missing required context for LocationRequestLink');
      }

      return postLocationShareRequestUrl(callerID, orgName, token);
    },
    mockResolveValue:
      'https://example.rapidsosportal.com/assist/locationrequest?token=<token>',
    display: 'Location Request Link',
    description: "A link to request the recipient's location.",
  },
  {
    key: 'NonEmergencyPhone',
    resolve: ({ nonEmergencyPhone }) => {
      if (!nonEmergencyPhone) {
        throw new Error('Missing required context for NonEmergencyPhone');
      }

      return (
        formatInternationalPhoneNumberHuman(nonEmergencyPhone) ??
        nonEmergencyPhone
      );
    },
    display: 'Non-Emergency Phone',
    description: 'The 24/7 non-emergency phone number for your agency.',
  },
  {
    key: 'RoadsideAssistanceLink',
    resolve: async ({ callerID, accountId }) => {
      if (!callerID || !accountId) {
        throw new Error('Missing required context for RoadsideAssistanceLink');
      }

      return postRoadsideAssistanceLink(callerID, accountId);
    },
    mockResolveValue: 'https://example.my-roadside.com/tl/<token>',
    display: 'Roadside Assistance Link',
    description:
      'A link to request lockout, flat tire, and/or roadside assistance for the recipient.',
    capability: ROADSIDE_ASSISTANCE,
  },
  {
    key: 'UserEmail',
    resolve: ({ userEmail }) => {
      if (!userEmail) {
        throw new Error('Missing required context for UserEmail');
      }

      return userEmail;
    },
    display: 'User Email',
    description: 'The email address of the currently logged-in user.',
  },
  {
    key: 'UserFirstName',
    resolve: ({ userFirstName }) => {
      if (!userFirstName) {
        throw new Error('Missing required context for UserFirstName');
      }

      return userFirstName;
    },
    display: 'User First Name',
    description: 'The first name of the currently logged-in user.',
  },
  {
    key: 'UserLastName',
    resolve: ({ userLastName }) => {
      if (!userLastName) {
        throw new Error('Missing required context for UserLastName');
      }

      return userLastName;
    },
    display: 'User Last Name',
    description: 'The last name of the currently logged-in user.',
  },
] as const satisfies SmartPhrase[];

export const getSmartKeyTextSyntax = (key: string): string => `{{${key}}}`;

// TalkJS message custom-field key carrying the resolved smart-phrase values
// that the backend must not translate (a JSON-encoded list of strings). Must
// match DO_NOT_TRANSLATE_CUSTOM_FIELD on the backend.
export const DO_NOT_TRANSLATE_CUSTOM_FIELD = 'do_not_translate';

/**
 * Resolve every smart phrase present in `text` and return both the interpolated
 * string and the exact resolved values keyed by phrase key. The values are
 * captured here (not re-resolved later) so token-bearing phrases such as links
 * match the text that was actually sent, and callers can look up a specific
 * phrase's resolved value by key.
 */
export const interpolateSmartPhrasesWithResolvedPhrases = async (
  text: string,
  ctx: SmartPhraseContext,
): Promise<{
  body: string;
  resolvedPhrases: Partial<Record<SmartPhraseKey, string>>;
}> => {
  const presentPhrases = smartPhrases.filter(p =>
    text.includes(getSmartKeyTextSyntax(p.key)),
  );

  const resolved = await Promise.all(
    presentPhrases.map(async p => {
      try {
        return { key: p.key, value: await p.resolve(ctx) };
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`Error resolving smart phrase ${p.key}:`, err);

        throw err;
      }
    }),
  );

  const body = resolved.reduce(
    (acc, entry) =>
      acc.replaceAll(getSmartKeyTextSyntax(entry.key), entry.value),
    text,
  );

  const resolvedPhrases = Object.fromEntries(
    resolved
      .filter(entry => entry.value)
      .map(entry => [entry.key, entry.value]),
  );

  return { body, resolvedPhrases };
};

export const interpolateSmartPhrases = async (
  text: string,
  ctx: SmartPhraseContext,
): Promise<string> => {
  const { body } = await interpolateSmartPhrasesWithResolvedPhrases(text, ctx);

  return body;
};

/**
 * Build the TalkJS send options that shield resolved smart-phrase values from
 * translation. Returns undefined when there is nothing to protect so the send
 * call is unchanged.
 */
export const buildDoNotTranslateSendOptions = (
  resolvedPhrases: Partial<Record<SmartPhraseKey, string>> | undefined,
): { custom: { [key: string]: string } } | undefined => {
  // Only the resolved values are protected from translation; the phrase keys
  // are irrelevant to the backend, so serialize a de-duplicated value list.
  const values = resolvedPhrases
    ? Array.from(new Set(Object.values(resolvedPhrases)))
    : [];

  return values.length
    ? {
        custom: {
          [DO_NOT_TRANSLATE_CUSTOM_FIELD]: JSON.stringify(values),
        },
      }
    : undefined;
};

export const parseSmartPhrases = (text: string): ParsedSegment[] => {
  const segments: ParsedSegment[] = [];
  const phraseRegex = /\{\{(\w+)\}\}/g;
  let lastIndex = 0;
  let match;

  // Find all {{Key}} patterns and split text around them
  while ((match = phraseRegex.exec(text)) !== null) {
    // Add text before the phrase
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    const key = match[1];

    // Add the phrase segment
    segments.push({
      type: 'phrase',
      key,
      phrase: smartPhrases.find(p => p.key === key),
    });

    lastIndex = phraseRegex.lastIndex;
  }

  // Add remaining text after last phrase
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  return segments;
};
